import type { RichInboxMessageProps } from '../components/RichInboxMessageCard.vue'

export type InboxMessage = {
  content: string
  id: string
  media: string
  payload?: {
    terms?: string
  }
  readAt: Maybe<null>
  title: string
  url: string
  executionDateTime: string
}

type RichInboxStore = {
  cid: Maybe<string>
  deletedMessageIds: string[]
  readMessageIds: string[]
  fetchedAt: Maybe<Date>
  messages: RichInboxMessageProps[]
  fetchState: Maybe<'error' | 'success'>
}

const BASE_URL = 'https://optimobile-inbox-srv-us.optimove.net'

const MEDIA_BASE_URL = 'https://i-us-east-1.app.delivery'
const MEDIA_SIZE = '440x160'

const toMediaUrl = (media: Maybe<string>): string =>
  media ? `${MEDIA_BASE_URL}/${MEDIA_SIZE}/${media.replace(/^\//, '')}` : ''

const TENANT_ID = '865'

const toSearchParams = (brandId: string, params?: Record<string, string>) =>
  new URLSearchParams({ BrandId: brandId, TenantId: TENANT_ID, ...params }).toString()

const DEFAULT_STORE: RichInboxStore = {
  cid: null,
  deletedMessageIds: [],
  readMessageIds: [],
  messages: [],
  fetchedAt: null,
  fetchState: null,
}

const STALE_THRESHOLD_MS = 30 * 60 * 1000

const toRichInboxMessage = (message: InboxMessage): RichInboxMessageProps => ({
  media: toMediaUrl(message.media),
  message: message.content,
  headline: message.title,
  id: message.id,
  link: message.url,
  read: !!message.readAt,
  terms: message.payload?.terms,
  timestamp: message.executionDateTime,
})

const sortDesc = (a: RichInboxMessageProps, b: RichInboxMessageProps) =>
  new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()

const toReportEvent = (messageId: string, customerId: string, eventType: string, uuid: string) => ({
  context: {
    containerId: 'Inbox',
    messageId,
  },
  customerId,
  eventType,
  timestamp: new Date().toISOString(),
  uuid,
})

export const useOptimove = () => {
  const store = useSessionStorage<RichInboxStore>(StateKey.RichInbox, DEFAULT_STORE)

  const isLoading = useState(() => false)

  const { log } = useDatadog()

  const { brandId } = useRuntimeConfig().public.optimove

  const { result: userDataResult } = useUserDataQuery({
    fetchPolicy: 'cache-first',
  })

  const customerId = computed(() => userDataResult.value?.player.customerId)

  const messages = computed(() => {
    const { deletedMessageIds, messages, readMessageIds } = store.value

    return messages
      .filter((message) => !deletedMessageIds.includes(message.id))
      .map((message) => ({ ...message, read: message.read || readMessageIds.includes(message.id) }))
      .sort(sortDesc)
  })

  const unreadMessagesCount = computed(
    () => messages.value.filter((message) => !message.read).length,
  )

  const state = computed(() => store.value.fetchState)

  const fetchMessages = async () => {
    if (isLoading.value) return

    try {
      isLoading.value = true

      if (!customerId.value || !brandId) {
        log('error', {
          component: 'optimove.ts',
          message: 'fetchMessages was called without customerId or brandId',
        })
        store.value.fetchState = 'error'
        return
      }

      const lastFetchedAt = store.value.fetchedAt
      if (
        store.value.cid === customerId.value &&
        store.value.fetchState === 'success' &&
        lastFetchedAt &&
        Date.now() - new Date(lastFetchedAt).getTime() < STALE_THRESHOLD_MS
      ) {
        return
      }

      resetReadMessages()
      resetDeletedMessages()

      const searchParams = toSearchParams(brandId, { CustomerId: customerId.value })

      const response = await $fetch<InboxMessage[]>(
        `${BASE_URL}/api/v1/messages/getusermessages?${searchParams}`,
        { retry: 0 },
      )
      store.value.fetchedAt = new Date()
      store.value.messages = response.map(toRichInboxMessage)
      store.value.cid = customerId.value
      store.value.fetchState = 'success'
    } catch (error) {
      store.value.fetchState = 'error'
      log('warn', {
        component: 'optimove.ts',
        error,
        message: 'failed to fetch inbox messages',
      })
    } finally {
      isLoading.value = false
    }
  }

  const resetReadMessages = () => (store.value.readMessageIds = [])

  const setReadMessage = (messageId: string) => {
    const readMessageIds = store.value.readMessageIds
    if (readMessageIds.includes(messageId)) return
    store.value.readMessageIds = [...readMessageIds, messageId]
  }

  const setReadMessageAll = () => {
    const readMessageIds = store.value.readMessageIds
    const unreadMessageIds = store.value.messages
      .filter(({ read, id }) => !read && !readMessageIds.includes(id))
      .map(({ id }) => id)

    if (unreadMessageIds.length <= 0) return
    store.value.readMessageIds = [...readMessageIds, ...unreadMessageIds]
  }

  const resetDeletedMessages = () => (store.value.deletedMessageIds = [])

  const setDeletedMessage = (messageId: string) => {
    const deletedMessageIds = store.value.deletedMessageIds
    if (deletedMessageIds.includes(messageId)) return
    store.value.deletedMessageIds = [...deletedMessageIds, messageId]
  }

  const syncReadAndDeletedMessages = async ({
    clickedMessageId,
  }: { clickedMessageId?: string } = {}) => {
    const cid = customerId.value
    if (!cid) {
      log('error', {
        component: 'optimove.ts',
        message: 'syncReadAndDeletedMessages was called without customerId',
      })
      throw new Error('no customerId provided')
    }

    if (
      !clickedMessageId &&
      store.value.readMessageIds.length === 0 &&
      store.value.deletedMessageIds.length === 0
    )
      return

    const searchParams = toSearchParams(brandId)

    try {
      await $fetch(`${BASE_URL}/api/v2/events/report?${searchParams}`, {
        method: 'POST',
        body: [
          ...store.value.readMessageIds.map((messageId) =>
            toReportEvent(messageId, cid, 'embedded-message.read', messageId),
          ),
          ...store.value.deletedMessageIds.map((messageId) =>
            toReportEvent(messageId, cid, 'embedded-message.deleted', messageId),
          ),
          // Read and deleted are re-sent on every sync, so their uuid stays stable. A click is
          // never stored, so it is sent once and gets a uuid of its own.
          ...(clickedMessageId
            ? [
                toReportEvent(
                  clickedMessageId,
                  cid,
                  'embedded-message.clicked',
                  crypto.randomUUID(),
                ),
              ]
            : []),
        ],
        retry: 0,
      })
    } catch (error) {
      log('warn', {
        component: 'optimove.ts',
        error,
        message: 'failed to sync read and deleted messages',
      })
    }
  }

  return {
    state,
    isLoading,
    messages,
    unreadMessagesCount,
    fetchMessages,
    syncReadAndDeletedMessages,
    setDeletedMessage,
    setReadMessage,
    setReadMessageAll,
  }
}
